/**
 * Intelligence Dashboard
 * Decision-engine dashboard with URL-based routing
 *
 * 4-Tab Flat Structure:
 * 1. Dashboard (Home) - /dashboard
 * 2. Tasks - /dashboard/tasks/*
 * 3. Classes - /dashboard/classes/* (Your Classes + Syllabi)
 * 4. Plan - /dashboard/plan
 */

import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PageContainer from '@/components/layout/PageContainer';
import { ScrollablePageWrapper } from '@/components/layout/ScrollablePageWrapper';
import { useSEO } from '@/utils/seo';

// Dashboard navigation and tab components
import { DashboardNavbar } from '@/components/dashboard/DashboardNavbar';
import {
  DashboardTabContent,
  TasksTabContent,
  ClassesTabContent,
  StudyPlanTabContent,
} from '@/components/dashboard/tabs';

export default function IntelligenceDashboard() {
  // SEO meta tags
  useSEO({
    title: 'Dashboard | JD Simplified',
    description: 'Your personalized learning dashboard. Track progress, see insights, and get AI-powered study recommendations.',
    url: 'https://jdsimplified.com/dashboard',
    noindex: true
  });

  return (
    <ScrollablePageWrapper navbar={<DashboardNavbar />}>
      <PageContainer disablePadding={false} className="pt-6 pb-6 md:pb-12 mx-auto px-4" maxWidth="6xl" noOverflow={true}>
        {/* URL-based Route Content */}
        <Suspense fallback={<div className="animate-pulse h-64 bg-muted rounded-lg" />}>
          <Routes>
            {/* Main tabs - flat structure */}
            <Route index element={<DashboardTabContent />} />
            <Route path="tasks/*" element={<TasksTabContent />} />
            <Route path="classes/*" element={<ClassesTabContent />} />
            <Route path="plan" element={<StudyPlanTabContent />} />

            {/* Redirects from old nested routes for backward compatibility */}
            <Route path="attack-plan" element={<Navigate to="/dashboard/tasks" replace />} />
            <Route path="attack-plan/tasks/*" element={<Navigate to="/dashboard/tasks" replace />} />
            <Route path="attack-plan/syllabi/*" element={<Navigate to="/dashboard/classes" replace />} />
            <Route path="attack-plan/plan" element={<Navigate to="/dashboard/plan" replace />} />
            <Route path="profile" element={<Navigate to="/settings/account" replace />} />
            <Route path="syllabus/*" element={<Navigate to="/dashboard/classes" replace />} />
            <Route path="study-plan" element={<Navigate to="/dashboard/plan" replace />} />
          </Routes>
        </Suspense>
      </PageContainer>
    </ScrollablePageWrapper>
  );
}
